// src/components/UserForm.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select"; // ✅ searchable dropdown
import { createUser, updateUser, getUser, getState } from "../http/api";
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import "../common/style.css";
import "./user.css";

export default function UserForm({ setToast, mode }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    address: "",
    state: "",
    roles: "", // Default to empty string for validation
    tpname: "",
    password: "",
    active: true,
    showPassword: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [stateOptions, setStateOptions] = useState([]);

  // Role options are static, so they can remain here
  const roleOptions = [
    { value: "TP", label: "TP" },
    { value: "ADMIN", label: "ADMIN" }
  ];

  useEffect(() => {
    localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    localStorage.setItem("sb|sidebar-toggle", !isSidebarOpen);
  };

  // Fetch states only once
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const stateResponse = await getState();
        const formattedStateOptions = stateResponse.data.map((s) => ({
          value: s.name,
          label: s.name,
        }));
        setStateOptions(formattedStateOptions);
      } catch (error) {
        if (setToast) {
          setToast("Failed to load states", "error");
        }
      }
    };
    fetchStates();
  }, [setToast]);

  // Fetch user only in edit mode
  useEffect(() => {
    const fetchUser = async () => {
      if (mode === "edit" && id) {
        try {
          const userResponse = await getUser(id);
          const user = userResponse.data;
          setFormData({
            name: user.name || "",
            email: user.email || "",
            mobile: user.mobile || "",
            address: user.address || "",
            state: user.state || "",
            roles: user.roles || "",
            tpname: user.tpname || "",
            active: user.active === true || user.active === 1,
            password: "",
            showPassword: false,
          });
        } catch (error) {
          if (setToast) {
            setToast("Failed to load user details", "error");
          }
          navigate("/users");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id, mode, setToast, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "name",
      "email",
      "mobile",
      "address",
      "state",
      "roles",
      "tpname",
    ];

    // Check all required fields
    requiredFields.forEach((field) => {
      if (
        !formData[field] ||
        (typeof formData[field] === "string" && !formData[field].trim())
      ) {
        newErrors[field] = `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required`;
      }
    });

    // Specific validation rules
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (formData.mobile && !/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Invalid mobile number (10 digits)";
    }
    if (mode === "add" && !formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      if (mode === "add") {
        await createUser(formData);
        if (setToast) {
          setToast("User created successfully", "success");
        }
      } else {
        await updateUser({ ...formData, id });
        if (setToast) {
          setToast("User updated successfully", "success");
        }
      }
      navigate("/users");
    } catch (error) {
      // Updated: Check if setToast exists before calling
      if (setToast) {
        setToast(
          `Failed to ${mode === "add" ? "create" : "update"} user`,
          "error"
        );
      } else {
        console.error(
          "setToast is not defined. The parent component did not pass it as a prop."
        );
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div
      className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}
    >
      <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
      <div id="layoutSidenav">
        <SideNave isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav_content">
          <main>
            <div className="container-fluid px-4">
              <h4 className="mt-4">
                {mode === "add" ? "Add New User" : "Edit User"}
              </h4>
              <div className="card mb-4">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Name, Email, Mobile in one row */}
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${
                            errors.name ? "is-invalid" : ""
                          }`}
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && (
                          <div className="invalid-feedback">{errors.name}</div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control form-control-sm ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                        {errors.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Mobile<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${
                            errors.mobile ? "is-invalid" : ""
                          }`}
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                        />
                        {errors.mobile && (
                          <div className="invalid-feedback">
                            {errors.mobile}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* State, Role, TP Name in one row */}
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          State<span className="text-danger">*</span>
                        </label>
                        <Select
                          options={stateOptions}
                          value={stateOptions.find(
                            (opt) => opt.value === formData.state
                          )}
                          onChange={(opt) =>
                            setFormData((p) => ({ ...p, state: opt.value }))
                          }
                          isSearchable
                          placeholder="Select State"
                          className={`${
                            errors.state ? "is-invalid-select" : ""
                          }`}
                        />
                        {errors.state && (
                          <div className="invalid-feedback d-block">
                            {errors.state}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Role<span className="text-danger">*</span>
                        </label>
                        <Select
                          options={roleOptions}
                          value={roleOptions.find(
                            (opt) => opt.value === formData.roles
                          )}
                          onChange={(opt) =>
                            setFormData((p) => ({ ...p, roles: opt.value }))
                          }
                          isSearchable
                          placeholder="Select Role"
                          className={`${
                            errors.roles ? "is-invalid-select" : ""
                          }`}
                        />
                        {errors.roles && (
                          <div className="invalid-feedback d-block">
                            {errors.roles}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          TP Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${
                            errors.tpname ? "is-invalid" : ""
                          }`}
                          name="tpname"
                          value={formData.tpname}
                          onChange={handleChange}
                        />
                        {errors.tpname && (
                          <div className="invalid-feedback">
                            {errors.tpname}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address + Active */}
                    <div className="row mb-3">
                      <div className="col-md-8">
                        <label className="form-label">
                          Address<span className="text-danger">*</span>
                        </label>
                        <textarea
                          className={`form-control form-control-sm ${
                            errors.address ? "is-invalid" : ""
                          }`}
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                        />
                        {errors.address && (
                          <div className="invalid-feedback">
                            {errors.address}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 d-flex align-items-center">
                        <div className="form-check mt-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleChange}
                          />
                          <label className="form-check-label">Active</label>
                        </div>
                      </div>
                    </div>

                    {/* Password (only add mode) */}
                    {mode === "add" && (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">
                            Password<span className="text-danger">*</span>
                          </label>
                          <div className="input-group input-group-sm">
                            <input
                              type={formData.showPassword ? "text" : "password"}
                              className={`form-control form-control-sm ${
                                errors.password ? "is-invalid" : ""
                              }`}
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                            />
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              type="button"
                              onClick={() =>
                                setFormData((p) => ({
                                  ...p,
                                  showPassword: !p.showPassword,
                                }))
                              }
                            >
                              {formData.showPassword ? "Hide" : "Show"}
                            </button>
                          </div>
                          {errors.password && (
                            <div className="invalid-feedback d-block">
                              {errors.password}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate("/users")}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary btn-sm">
                        {mode === "add" ? "Create User" : "Update User"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
